import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function useAutoSave(saveFn: () => void, hasChanges: boolean, options?: { delay?: number }) {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const resetRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const delay = options?.delay ?? 1500;

  // Debounced auto-save
  useEffect(() => {
    if (!hasChanges) return;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setStatus('saving');
      try {
        saveFn();
      } catch {
        setStatus('error');
      }
    }, delay);

    return () => clearTimeout(timeoutRef.current);
  }, [hasChanges, saveFn, delay]);

  // Reset "saved" status after 3 seconds
  useEffect(() => {
    if (status === 'saved') {
      resetRef.current = setTimeout(() => setStatus('idle'), 3000);
      return () => clearTimeout(resetRef.current);
    }
  }, [status]);

  // Called by the parent when save mutation succeeds/fails
  const onSaveSuccess = useCallback(() => setStatus('saved'), []);
  const onSaveError = useCallback(() => setStatus('error'), []);

  // Ctrl+S / Cmd+S manual trigger — only save if there are valid changes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (!hasChanges) return;
        saveFn();
        setStatus('saving');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveFn, hasChanges]);

  return { status, onSaveSuccess, onSaveError };
}
